import api from 'services/api';

// The four clear endpoints, which the UI calls "discard".
//
//   POST /process_event_instances/:id/clear
//   POST /process_event_instances/:zone_name/:name/clear
//   POST /file_event_instances/:id/clear
//   POST /file_event_instances/:zone_name/:name/clear
//
// "Discard" is the UI's word for one effect: reduce the surplus without
// anything having consumed it. The backend calls all of this "clear", which
// there is the umbrella term for every way an occurrence stops being
// outstanding -- consumption included. The two vocabularies are kept apart
// deliberately; see the Event History outcome values for the same distinction.
//
// The two kinds are NOT symmetric, and the surplus modal has to reflect it:
//
//   * A process occurrence is interchangeable. The backend clears the OLDEST
//     raised instance of the parent event whatever id you pass -- the route is
//     a compatibility shim that looks the event up from the instance id and
//     then applies FIFO. So the honest UI affordance is "discard one", which
//     decrements the surplus, not "discard THIS one".
//   * A file occurrence is a distinct named arrival. Its route clears exactly
//     the row addressed, so a specific item can be discarded.
//
// No request body anywhere: the clearing user comes from the auth token.

/** Ids the backend actually cleared. Empty when there was nothing outstanding. */
export interface DiscardedProcessOccurrences {
  readonly message: string;
  readonly cleared_event_instance_ids: readonly number[];
}

export interface DiscardedFileOccurrence {
  readonly message: string;
  readonly file_event_instance?: unknown;
}

/**
 * Discard one process occurrence — in practice the OLDEST outstanding one for
 * this occurrence's event, regardless of which id is passed. Pass the oldest
 * occurrence's id so the row that disappears is the row the operator was
 * looking at. Throws on HTTP error.
 */
export const discardProcessOccurrence = async (eventInstanceId: number): Promise<DiscardedProcessOccurrences> => {
  const res = await api.post(`/process_event_instances/${encodeURIComponent(eventInstanceId)}/clear`);
  return res.data as DiscardedProcessOccurrences;
};

/** Discard every outstanding process occurrence of one event. Throws on HTTP error. */
export const discardAllProcessOccurrences = async (zoneName: string, name: string): Promise<DiscardedProcessOccurrences> => {
  const res = await api.post(`/process_event_instances/${encodeURIComponent(zoneName)}/${encodeURIComponent(name)}/clear`);
  return res.data as DiscardedProcessOccurrences;
};

/** Discard one specific file arrival, addressed by its own id. Throws on HTTP error. */
export const discardFileOccurrence = async (fileEventInstanceId: number): Promise<DiscardedFileOccurrence> => {
  const res = await api.post(`/file_event_instances/${encodeURIComponent(fileEventInstanceId)}/clear`);
  return res.data as DiscardedFileOccurrence;
};

/** Discard every unprocessed file arrival of one event. Throws on HTTP error. */
export const discardAllFileOccurrences = async (zoneName: string, name: string): Promise<DiscardedFileOccurrence> => {
  const res = await api.post(`/file_event_instances/${encodeURIComponent(zoneName)}/${encodeURIComponent(name)}/clear`);
  return res.data as DiscardedFileOccurrence;
};
